/**
 * Canvas geometry: turning measured rects back into document positions.
 *
 * The canvas renders the *real* compiled email in an iframe and paints an
 * absolutely-positioned overlay on top, rather than re-implementing each block
 * as a React component. That keeps WYSIWYG true by construction — the thing
 * being edited is the thing that gets sent — but it means selection and
 * drag-and-drop have no component tree to interrogate. They have rects.
 *
 * This module is the bridge, and it is deliberately pure: no DOM, no React.
 * The overlay measures `[data-cvn-id]` / `[data-cvn-slot]` elements and hands
 * the rects here; every hit-test and drop decision is then unit-testable with
 * synthetic geometry.
 *
 * All rects are in **overlay coordinates** — the caller translates from iframe
 * content coordinates once, when measuring.
 */
import { Block, EmailDocument } from './document';
import { BlockLocation, findLocation, isDescendant, slotsOf } from './tree';

export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface BlockRect {
  blockId: string;
  rect: Rect;
}

/** A container's slot region, from a `[data-cvn-slot]` element. */
export interface SlotRect {
  parentId: string;
  slot: number;
  rect: Rect;
}

export interface CanvasMeasurements {
  blocks: BlockRect[];
  /**
   * Slot regions, when the components expose them. Without these an *empty*
   * slot has no geometry at all — nothing rendered inside it to measure — so it
   * cannot be a canvas drop target and the layers tree is the only way in.
   */
  slots?: SlotRect[];
  /** The content area, used as the root drop region. */
  rootRect?: Rect;
}

/**
 * Reads a `data-cvn-slot` value (`"<blockId>:<slotIndex>"`).
 *
 * Returns null for anything malformed rather than throwing: the value comes from
 * rendered partner-editable Twig, so a fragment override can produce nonsense,
 * and a bad marker should cost one drop target rather than the whole canvas.
 * Splits on the *last* colon so a block id containing one would still parse.
 */
export function parseSlotMarker(
  value: string,
): { parentId: string; slot: number } | null {
  const separator = value.lastIndexOf(':');
  if (separator <= 0 || separator === value.length - 1) return null;

  const parentId = value.slice(0, separator);
  const raw = value.slice(separator + 1);
  if (!/^\d+$/.test(raw)) return null;

  return { parentId, slot: Number(raw) };
}

/**
 * A raw measurement taken from one element inside the canvas iframe.
 *
 * The caller reads the two marker attributes and `getBoundingClientRect()`; this
 * module does the interpretation, so the DOM-touching part stays a thin loop and
 * the rules below stay testable.
 */
export interface RawMeasurement {
  /** `data-cvn-id`, when present. */
  blockId?: string | null;
  /** `data-cvn-slot` verbatim (`"<blockId>:<slotIndex>"`), when present. */
  slotMarker?: string | null;
  /** Rect in **iframe content coordinates**. */
  rect: Rect;
}

/**
 * A comment marker found in the canvas: `cvn:<id>` opens, `/cvn:<id>` closes.
 *
 * Read from the DOM by `CanvasFrame`, paired here so the pairing rules are testable
 * without a document.
 */
export interface CommentMarker {
  blockId: string;
  kind: 'start' | 'end';
}

/** `cvn:b1` / `/cvn:b1` → the marker it is, or null for any other comment. */
export function parseCommentMarker(text: string): CommentMarker | null {
  const match = /^\s*(\/?)cvn:([A-Za-z0-9_-]+)\s*$/.exec(text);
  if (!match) return null;
  return { blockId: match[2], kind: match[1] ? 'end' : 'start' };
}

/**
 * Pairs markers into `[startIndex, endIndex]` ranges by block id.
 *
 * Nested blocks produce nested pairs, so this matches each `end` to the **most recent
 * unclosed `start` with the same id** rather than assuming a flat sequence. A start with
 * no end, or an end with no start, is dropped: the body is generated, but a hand edit in
 * the Source view can leave one behind, and half a range has no meaningful rect.
 */
export function pairCommentMarkers(
  markers: (CommentMarker & { index: number })[],
): { blockId: string; start: number; end: number }[] {
  const open = new Map<string, number[]>();
  const pairs: { blockId: string; start: number; end: number }[] = [];

  for (const marker of markers) {
    if (marker.kind === 'start') {
      const stack = open.get(marker.blockId) ?? [];
      stack.push(marker.index);
      open.set(marker.blockId, stack);
      continue;
    }

    const stack = open.get(marker.blockId);
    const start = stack?.pop();
    if (start === undefined) continue;
    pairs.push({ blockId: marker.blockId, start, end: marker.index });
  }

  return pairs;
}

export interface MeasurementOffset {
  /** Added to every rect: the iframe's position within the overlay. */
  x: number;
  y: number;
}

function translate(rect: Rect, offset: MeasurementOffset): Rect {
  return {
    top: rect.top + offset.y,
    left: rect.left + offset.x,
    width: rect.width,
    height: rect.height,
  };
}

/**
 * Assembles `CanvasMeasurements` from raw element measurements.
 *
 * Rects arrive in iframe content coordinates and are translated into overlay
 * space here — once, at the boundary — so every consumer downstream works in a
 * single coordinate system.
 *
 * One element can carry both markers: a container renders `data-cvn-id` on its
 * outer element and `data-cvn-slot` on a descendant, but a partner fragment
 * override is free to put both on the same node, so both are read independently
 * rather than assumed exclusive.
 *
 * Zero-area rects are dropped. A component whose content is empty (an unfilled
 * text block, a collapsed row) measures 0×0, and such a rect can never contain a
 * point — keeping them would only add hit-test candidates that never match and
 * slot regions that can never be dropped into.
 */
export function collectMeasurements(
  raw: RawMeasurement[],
  offset: MeasurementOffset = { x: 0, y: 0 },
  rootRect?: Rect,
): CanvasMeasurements {
  const blocks: BlockRect[] = [];
  const slots: SlotRect[] = [];

  for (const entry of raw) {
    if (rectArea(entry.rect) === 0) continue;
    const rect = translate(entry.rect, offset);

    if (entry.blockId) {
      blocks.push({ blockId: entry.blockId, rect });
    }
    if (entry.slotMarker) {
      const parsed = parseSlotMarker(entry.slotMarker);
      if (parsed) {
        slots.push({ parentId: parsed.parentId, slot: parsed.slot, rect });
      }
    }
  }

  return {
    blocks,
    slots,
    rootRect: rootRect ? translate(rootRect, offset) : undefined,
  };
}

export function containsPoint(rect: Rect, point: Point): boolean {
  return (
    point.x >= rect.left &&
    point.x <= rect.left + rect.width &&
    point.y >= rect.top &&
    point.y <= rect.top + rect.height
  );
}

export function rectArea(rect: Rect): number {
  return Math.max(0, rect.width) * Math.max(0, rect.height);
}

/** Smallest rect enclosing all inputs, or null when given none. */
export function unionRect(rects: Rect[]): Rect | null {
  if (rects.length === 0) return null;

  let top = Infinity;
  let left = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;

  for (const rect of rects) {
    top = Math.min(top, rect.top);
    left = Math.min(left, rect.left);
    right = Math.max(right, rect.left + rect.width);
    bottom = Math.max(bottom, rect.top + rect.height);
  }
  return { top, left, width: right - left, height: bottom - top };
}

/** Block id → depth, root blocks being 0. */
export function depthMap(blocks: Block[], depth = 0, into = new Map<string, number>()): Map<string, number> {
  for (const block of blocks) {
    into.set(block.id, depth);
    for (const slot of slotsOf(block)) {
      depthMap(slot, depth + 1, into);
    }
  }
  return into;
}

/**
 * The block under a point — the *innermost* one.
 *
 * A nested block's rect sits inside its ancestors', so several rects contain the
 * point and picking the first would always select the outermost section. Depth
 * comes from the document rather than from rect area: a container whose only
 * child overflows it would otherwise resolve the wrong way round. Area breaks
 * ties between siblings at equal depth (overlapping rects, which columns can
 * produce at narrow widths).
 */
export function blockAtPoint(
  measurements: CanvasMeasurements,
  document: EmailDocument,
  point: Point,
): string | null {
  const depths = depthMap(document.blocks);

  let best: { id: string; depth: number; area: number } | null = null;
  for (const { blockId, rect } of measurements.blocks) {
    if (!containsPoint(rect, point)) continue;

    const depth = depths.get(blockId);
    if (depth === undefined) continue; // measured but no longer in the document

    const area = rectArea(rect);
    if (
      !best ||
      depth > best.depth ||
      (depth === best.depth && area < best.area)
    ) {
      best = { id: blockId, depth, area };
    }
  }
  return best ? best.id : null;
}

export type DropIndicator =
  /** Insertion line between (or at the edge of) existing children. */
  | { type: 'line'; rect: Rect }
  /** The whole target region, for an empty slot. */
  | { type: 'box'; rect: Rect };

export interface DropTarget {
  location: BlockLocation;
  indicator: DropIndicator;
}

/**
 * Thickness of the drawn insertion line, in overlay pixels.
 *
 * 3, not 2: at 2 px the line was genuinely hard to find on a busy email — it was
 * *also* the only thing a drag showed, before the drag chip. The end caps in
 * `CanvasFrame.less` do the rest of the work.
 */
const INDICATOR_THICKNESS = 3;

function lineAbove(rect: Rect): Rect {
  return {
    top: rect.top - INDICATOR_THICKNESS / 2,
    left: rect.left,
    width: rect.width,
    height: INDICATOR_THICKNESS,
  };
}

function lineBelow(rect: Rect): Rect {
  return {
    top: rect.top + rect.height - INDICATOR_THICKNESS / 2,
    left: rect.left,
    width: rect.width,
    height: INDICATOR_THICKNESS,
  };
}

function childrenAt(
  document: EmailDocument,
  parentId: string | null,
  slot: number,
): Block[] {
  if (parentId === null) return document.blocks;

  const find = (blocks: Block[]): Block[] | null => {
    for (const block of blocks) {
      if (block.id === parentId) return slotsOf(block)[slot] ?? [];
      for (const children of slotsOf(block)) {
        const hit = find(children);
        if (hit) return hit;
      }
    }
    return null;
  };
  return find(document.blocks) ?? [];
}

/**
 * The innermost slot region containing the point, or null for the document root.
 *
 * Prefers explicit `[data-cvn-slot]` rects. Falling back to the union of a
 * slot's children would make an empty slot untargetable *and* would let a
 * child's own rect stand in for its parent's slot, so explicit rects are what
 * make dropping into an empty column work at all.
 */
function slotAtPoint(
  measurements: CanvasMeasurements,
  document: EmailDocument,
  point: Point,
): { parentId: string | null; slot: number } {
  const depths = depthMap(document.blocks);

  let best: { parentId: string; slot: number; depth: number; area: number } | null =
    null;

  for (const entry of measurements.slots ?? []) {
    if (!containsPoint(entry.rect, point)) continue;
    const depth = depths.get(entry.parentId);
    if (depth === undefined) continue;

    const area = rectArea(entry.rect);
    if (
      !best ||
      depth > best.depth ||
      (depth === best.depth && area < best.area)
    ) {
      best = { parentId: entry.parentId, slot: entry.slot, depth, area };
    }
  }

  return best
    ? { parentId: best.parentId, slot: best.slot }
    : { parentId: null, slot: 0 };
}

export interface DropOptions {
  /**
   * The block being dragged. It and its descendants are excluded as targets: a
   * container cannot be dropped inside itself, and the indicator must never
   * suggest it.
   */
  draggingId?: string;
}

/**
 * How close to a container's own top/bottom edge counts as "outside it".
 *
 * Without this band a container's first and last position are the *only* things
 * reachable near its edges, because the innermost slot always wins — so on an email
 * whose outermost block is a full-width section there was **no** way to drop
 * anything before or after it. Found by dragging: aiming at the very top of the
 * email inserted into the section instead of above it.
 *
 * 10 px is about a pointer's worth of aim. Inside the band the drop goes to the
 * container's own level; past it, into the container. The cost is that the first
 * position *inside* a container needs aiming 10 px lower, which is easy — while
 * "before the outermost block" was previously impossible.
 */
const EDGE_BAND = 10;

function slotRegion(
  measurements: CanvasMeasurements,
  parentId: string | null,
  slot: number,
): Rect | null {
  if (parentId === null) return measurements.rootRect ?? null;
  return (
    (measurements.slots ?? []).find(
      (entry) => entry.parentId === parentId && entry.slot === slot,
    )?.rect ?? null
  );
}

/**
 * Walks out of nested containers while the point hugs their edges.
 *
 * Only the *level* is escalated — the insertion index still comes from the ordinary
 * midpoint scan below, which is already correct once the level is right: the point
 * is inside the container, so comparing it to the container's own midpoint puts the
 * drop before or after it.
 *
 * **One level only.** Climbing all the way out whenever each edge coincides would
 * read the top of a column inside a section as "before the section", when the
 * obvious reading is "before the columns block". One step answers the case that was
 * actually broken — a full-width outermost container — and keeps nested aim honest.
 */
function levelAtPoint(
  measurements: CanvasMeasurements,
  document: EmailDocument,
  point: Point,
  level: { parentId: string | null; slot: number },
): { parentId: string | null; slot: number } {
  if (level.parentId === null) return level;

  // An empty slot is *only* a drop target — there is nothing to insert relative to,
  // so an edge band there would just make it harder to hit.
  if (childrenAt(document, level.parentId, level.slot).length === 0) return level;

  const region = slotRegion(measurements, level.parentId, level.slot);
  if (!region) return level;

  const hugsEdge =
    point.y - region.top <= EDGE_BAND ||
    region.top + region.height - point.y <= EDGE_BAND;
  if (!hugsEdge) return level;

  // The container's own place in the tree is where the drop goes instead.
  const outer = findLocation(document.blocks, level.parentId);
  return outer ? { parentId: outer.parentId, slot: outer.slot } : level;
}

/**
 * Where a drop at `point` would land, plus the indicator to draw.
 *
 * Children of a slot always stack vertically — that is how email blocks lay out
 * — so the insertion index compares the point against each child's vertical
 * midpoint. Which *slot* the point is in is decided by the slot rects, which is
 * how a horizontal column grid is handled without special-casing it here.
 */
export function dropTargetAtPoint(
  measurements: CanvasMeasurements,
  document: EmailDocument,
  point: Point,
  options: DropOptions = {},
): DropTarget | null {
  const { draggingId } = options;

  let { parentId, slot } = levelAtPoint(
    measurements,
    document,
    point,
    slotAtPoint(measurements, document, point),
  );

  // Dropping into the dragged block's own subtree is illegal; walking out to the
  // root keeps the gesture live (the drop lands in an ancestor) instead of the
  // indicator vanishing while the pointer is inside the dragged element.
  if (
    draggingId &&
    parentId !== null &&
    (parentId === draggingId || isDescendant(document.blocks, draggingId, parentId))
  ) {
    parentId = null;
    slot = 0;
  }

  const rectsById = new Map(
    measurements.blocks.map((entry) => [entry.blockId, entry.rect]),
  );

  const siblings = childrenAt(document, parentId, slot)
    .filter((block) => block.id !== draggingId)
    .map((block) => ({ block, rect: rectsById.get(block.id) }))
    .filter((entry): entry is { block: Block; rect: Rect } => !!entry.rect);

  if (siblings.length === 0) {
    const region =
      (measurements.slots ?? []).find(
        (entry) => entry.parentId === parentId && entry.slot === slot,
      )?.rect ?? measurements.rootRect;

    if (!region) return null;
    return {
      location: { parentId, slot, index: 0 },
      indicator: { type: 'box', rect: region },
    };
  }

  for (let i = 0; i < siblings.length; i += 1) {
    const { rect } = siblings[i];
    if (point.y < rect.top + rect.height / 2) {
      return {
        location: { parentId, slot, index: indexInDocument(document, parentId, slot, siblings[i].block.id) },
        indicator: { type: 'line', rect: lineAbove(rect) },
      };
    }
  }

  const last = siblings[siblings.length - 1];
  return {
    location: {
      parentId,
      slot,
      index:
        indexInDocument(document, parentId, slot, last.block.id) + 1,
    },
    indicator: { type: 'line', rect: lineBelow(last.rect) },
  };
}

/**
 * A block's index in the *document*, not among the filtered siblings.
 *
 * The dragged block is filtered out of the sibling list for hit-testing, but the
 * returned index is consumed by `moveBlock`, which interprets it against the
 * real pre-removal list. Returning a filtered index would put the block one
 * position early on every backwards drag.
 */
function indexInDocument(
  document: EmailDocument,
  parentId: string | null,
  slot: number,
  blockId: string,
): number {
  const children = childrenAt(document, parentId, slot);
  const index = children.findIndex((block) => block.id === blockId);
  return index === -1 ? children.length : index;
}

/**
 * The bands the **layout** contributes: everything inside the email that is not
 * part of any block.
 *
 * A layout wraps `{% block content %}` in its own chrome — `core/layout/base@1`
 * puts a logo band above and a social/legal band below. None of it is a block, so
 * none of it has a `data-cvn-id`, so hovering it does nothing and there is no way
 * to tell "not selectable" from "selection is broken".
 *
 * Derived rather than measured separately: the union of every block rect is the
 * content span, and what is left above and below it inside the root *is* the
 * layout. That needs no new markers in the layout twig, so a partner's own layout
 * gets the same treatment as the built-in ones.
 *
 * Bands under a few pixels are dropped — those are padding rounding, not chrome.
 */
export function layoutBands(measurements: CanvasMeasurements): Rect[] {
  const root = measurements.rootRect;
  if (!root) return [];

  const content = unionRect(measurements.blocks.map((entry) => entry.rect));
  if (!content) return [root];

  const bands: Rect[] = [];
  const MIN = 4;

  const topHeight = content.top - root.top;
  if (topHeight >= MIN) {
    bands.push({ top: root.top, left: root.left, width: root.width, height: topHeight });
  }

  const bottomTop = content.top + content.height;
  const bottomHeight = root.top + root.height - bottomTop;
  if (bottomHeight >= MIN) {
    bands.push({ top: bottomTop, left: root.left, width: root.width, height: bottomHeight });
  }

  return bands;
}
